<template>
  <v-card>
    <v-card-title class="justify-center text-h6 text-break text-center">{{
      title
    }}</v-card-title>
    <v-card-text class="px-0">
      <div class="player" v-if="!videoEnded">
        <n-s-player
          v-if="product"
          :key="`${item?.id}-${video_data}`"
          :video_data="video_data"
          :session_id="
            ['company-patient', 'session-by-url', 'patient-sessions'].includes(
              this.$route.name
            )
              ? `${this.item?.id}${this.patient_id ?? ''}`
              : undefined
          "
          :view_progress="item.viewProgress"
          @videoEnded="videoEndedHandler"
          @updateViewProgress="updateViewProgress"
        />
      </div>
      <div v-else class="text-center pl-4 mt-4">
        <h2>Спасибо за просмотр!</h2>
      </div>
      <v-card class="text-center d-flex flex-column justify-center" flat>
        <v-rating
          v-if="!rated"
          color="yellow darken-3"
          background-color="yellow darken-3"
          half-increments
          hover
          large
          class="ma-auto mt-4"
          @input="ratingChange($event)"
        >
        </v-rating>
        <div v-if="rated" class="mt-4">Спасибо за отзыв!</div>
        <div class="text-center text-sm-caption pl-4 mt-4" v-if="rating">
          <template v-if="rating.count">
            Рейтинг: {{ rating.average.toFixed(1) }} / Оценок:
            {{ rating.count }}
          </template>
          <template v-else>Оценок пока нет</template>
        </div>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NSPlayer from "@/components/ns-player/NSPlayer";
import ApiService from "@/api/ApiService";

export default {
  name: "SessionForm",
  components: { NSPlayer },
  data() {
    return {
      rated: false,
      selected: [],
      videoEnded: false,
      // Свежий агрегат, который вернул бэк в ответ на оценку — чтобы число
      // на экране обновилось сразу, без перезагрузки визита.
      ratingOverride: null,
    };
  },
  computed: {
    ...mapGetters("modals", ["modalFormComponent"]),
    ...mapGetters("products", ["products"]),

    rating() {
      if (this.ratingOverride) return this.ratingOverride;
      if (!this.product) return null;
      // rating_avg приезжает из Postgres как NUMERIC — pg отдаёт его строкой,
      // поэтому приводим явно, иначе toFixed в шаблоне падает.
      return {
        average:
          this.product.rating_avg != null
            ? Number(this.product.rating_avg)
            : null,
        count: Number(this.product.rating_count) || 0,
      };
    },
    product() {
      if (this.item?.product) return this.item.product;
      const fromVuex = this.products.find(
        (product) => product.kinescope_id === this.item?.kinescope_id
      );
      if (fromVuex) return fromVuex;
      if (this.item?.kinescope_id) {
        return {
          id: null,
          kinescope_id: this.item.kinescope_id,
        };
      }
      return null;
    },
    video_data() {
      return this.product.kinescope_id;
    },
    title() {
      return this.modalFormComponent?.title;
    },
    desc() {
      return this.modalFormComponent?.desc;
    },
    item() {
      return {
        ...this.modalFormComponent?.item,
      };
    },
    patient_id() {
      return this.modalFormComponent?.patient_id;
    },
    viewProgress() {
      return this.item?.viewProgress;
    },
  },
  methods: {
    ...mapActions("products", ["request_products"]),
    doPlaySession() {},
    async ratingChange(value) {
      // Раньше фронт сам пересчитывал сумму и слал её PUT'ом на продукт —
      // из публичной ссылки это к тому же падало в 403. Теперь отправляем
      // саму оценку, агрегат считает бэк (см. N9 в DB_REFACTORING.md).
      if (!this.item?.public_uuid) {
        console.error("Ошибка при оценке: visit public_uuid не задан");
        return;
      }
      this.rated = true;
      try {
        const { data } = await ApiService.post(
          `v1/patients/visits/${this.item.public_uuid}/rating`,
          { score: value }
        );
        this.ratingOverride = data;
      } catch (e) {
        console.error("Ошибка при отправке оценки:", e);
      }
    },
    async videoEndedHandler() {
      this.videoEnded = true;

      if (!this.item.public_uuid) {
        console.error(
          "Ошибка при завершении просмотра: visit public_uuid не задан"
        );
        return;
      }

      try {
        await ApiService.post(
          `v1/patients/visits/${this.item.public_uuid}/view-end`
        );
      } catch (error) {
        console.error("Ошибка при завершении просмотра:", error);
      }
    },
    doSave(item) {
      const saveMethod = this.modalFormComponent?.saveMethod;
      if (saveMethod) {
        if (item.viewProgress === undefined)
          item.viewProgressFormatted = undefined;
        saveMethod(item).then(() => {
          this.modalFormComponent.item = { ...item };
        });
      }
    },

    formatTime(viewProgress) {
      if (viewProgress === undefined) return undefined;
      const roundedSeconds = Math.round(viewProgress);
      const minutes = Math.floor(roundedSeconds / 60);

      const remainingSeconds = roundedSeconds % 60;
      const formattedSeconds =
        remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds;
      return `${minutes}:${formattedSeconds}`;
    },

    async updateViewProgress(data) {
      const viewProgress = typeof data === "object" ? data.currentTime : data;

      this.item.viewProgress = viewProgress.toFixed(3);
      this.item.viewProgressFormatted = this.formatTime(viewProgress);

      if (!this.item.public_uuid) {
        console.error(
          "Ошибка при обновлении времени просмотра: visit public_uuid не задан"
        );
        return;
      }

      try {
        await ApiService.post(
          `v1/patients/visits/${this.item.public_uuid}/watchtime`,
          { watchtime: parseFloat(viewProgress.toFixed(2)) }
        );
      } catch (error) {
        console.error("Ошибка при обновлении времени просмотра:", error);
      }
    },
  },
  beforeMount() {
    // product может уже быть в item.product (новый flow с patient_visits).
    // Vuex products дёргаем только если нет inline product — для совместимости
    // со старым админ-флоу где SessionForm открывают без item.product.
    if (!this.item?.product) {
      this.request_products();
    }
  },
};
</script>

<style scoped lang="scss">
.player {
  align-items: center;
  justify-content: center;
  text-align: center;
  display: flex;
  margin: auto 10px;
}
</style>
